/**
 * Waiting for a field read that is no longer held open by its own request.
 *
 * **The number this exists for belongs to Netlify.** The frontend is served
 * there and reaches the API through a proxy rewrite (`netlify.toml`), and a
 * proxy rewrite times out after **26 seconds** — documented, and not
 * configurable. Reading PFF's FCS template takes far longer than that: measured
 * on 2026-11-03, `defense/pass_rush` answered 504 after 30 to 60 seconds and
 * 200 only on a later attempt, so one successful read of that one address took
 * about two minutes of retrying inside the backend.
 *
 * What the reviewer saw was "Pass Rush: Something went wrong." — the bodiless
 * fallback in `api.ts`, because the response came from the edge rather than
 * from the API. What the server log showed was the same address being read
 * twice over, interleaved: the edge answered the browser early, `withRetry`
 * started a second read, and the first was still running underneath it.
 *
 * Development never saw any of it, because Vite's proxy has no timeout at all
 * and the two-minute read simply finished.
 *
 * So the read is started, and then polled. Every HTTP call this makes returns
 * in milliseconds and none of them is near the cap.
 *
 * **No React, no `fetch`, and no sibling imported for its value**,
 * deliberately. `npm test` is bare `node --experimental-strip-types` over
 * `tests/` and `tsconfig` sets `allowImportingTsExtensions: false`, so a
 * *value* import of `./api` here is one node cannot resolve and this policy
 * would be untestable — which for the rule that decides how long a reviewer
 * waits is the one thing it must not be. Every other tested module in `src`
 * imports its siblings `import type` for the same reason.
 *
 * So the error constructor arrives as an argument. That is not a seam for
 * tests to substitute at: the tests pass the **real** `ApiError`, which they
 * may because `tests/` is outside `tsconfig.include` and can spell the
 * extension. There is one code path here and it is the deployed one.
 */

import type { ApiError } from "./api";

export type DiscoveryJobStatus = "in_progress" | "succeeded" | "failed";

/** The API's own failure shape, carried on a job that did not succeed. */
export interface DiscoveryJobFailure {
  /** What the synchronous route would have answered. `retry.ts` reads it. */
  status: number;
  code: string;
  message: string;
  field?: string | null;
  details?: Record<string, unknown>;
}

export interface DiscoveryJob<T> {
  id: string;
  status: DiscoveryJobStatus;
  result: T | null;
  error: DiscoveryJobFailure | null;
}

/**
 * How long to wait before the next poll, in milliseconds.
 *
 * Starts fast and settles, because the two cases are so far apart. A cached
 * address answers in well under a second and a reviewer watching thirteen of
 * them in a row feels every extra wait; `defense/pass_rush` takes two minutes
 * and does not care whether it is asked 40 times or 400.
 *
 * Capped at three seconds, which bounds how stale the screen can be at the
 * moment the read finishes. A two-minute read costs about fifty polls — each
 * one a few hundred bytes, none of them near anybody's timeout.
 */
export function pollDelayMs(attempt: number): number {
  return Math.min(400 * 1.5 ** (attempt - 1), 3000);
}

/**
 * How long to go on polling before giving up on a read.
 *
 * Generous, because the honest upper bound is large: "Re-read source" reads
 * every Feed on the profile behind one job, which for PFF's FCS template is
 * thirteen addresses, and the reads are serialised on one worker that another
 * operator may be ahead of.
 *
 * **Giving up is cheap and that is what makes a bound safe here.** Asking
 * again starts no second read: the server joins a submission to the read
 * already running, by fingerprint, so a reviewer who presses the button again
 * re-attaches to the same work rather than doubling it. Without that join this
 * number would have to be "never".
 */
export const POLL_TIMEOUT_MS = 20 * 60 * 1000;

const realSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Builds the error a failed read is raised as — in practice `ApiError` itself.
 *
 * Required rather than defaulted, so there is no second construction that only
 * the tests ever see. `retry.ts` recognises a failure structurally, by a
 * numeric `status` beside a string `code`, and that is exactly what this has
 * to produce.
 */
export type FailureFactory = (
  status: number,
  code: string,
  message: string,
  field: string | null,
  details: Record<string, unknown>,
) => ApiError;

export interface AwaitJobOptions {
  fail: FailureFactory;
  /** Told how long the read has been going, for anything showing progress. */
  onPoll?: (elapsedMs: number) => void;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
  timeoutMs?: number;
  /**
   * Abandons the wait when the screen that started it has gone.
   *
   * Without it a read left by navigating away goes on polling for the full
   * twenty minutes — hundreds of requests for an answer nobody will see, and
   * state set on a component that no longer exists. The read itself is not
   * cancelled, which is correct: the server finishes it, stores it, and the
   * next submission of the same read joins it.
   */
  signal?: { aborted: boolean };
  /**
   * How many *consecutive* failed polls to absorb before giving up.
   *
   * **A poll is not the read.** The read may be four minutes into a PFF
   * template while one `GET` on the way to asking about it meets a blip, and
   * treating that as the read failing throws away work the server has already
   * done — then charges the publisher for all of it again, because the
   * fingerprint join only covers reads still in flight.
   *
   * Consecutive, so a flaky connection cannot accumulate its way to a false
   * failure across a long read; the count resets on every answer.
   */
  maxPollFailures?: number;
}

/**
 * Start a read, wait for it, and answer as though it had been synchronous.
 *
 * A failed job is **raised as an `ApiError` carrying the server's own status
 * and code**, which is the whole contract with `retry.ts`: that policy decides
 * whether asking again could answer differently, and it decides it from the
 * code and the status rather than the prose. A job that reported only a
 * message would make a rejected credential and a dropped gateway look alike to
 * the one thing that has to tell them apart.
 */
export async function awaitDiscoveryJob<T>(
  start: () => Promise<DiscoveryJob<T>>,
  read: (jobId: string) => Promise<DiscoveryJob<T>>,
  {
    fail,
    onPoll,
    sleep = realSleep,
    now = Date.now,
    timeoutMs = POLL_TIMEOUT_MS,
    signal,
    maxPollFailures = 3,
  }: AwaitJobOptions,
): Promise<T> {
  const began = now();
  let job = await start();
  let attempt = 0;
  let consecutiveFailures = 0;

  while (job.status === "in_progress") {
    if (signal?.aborted) {
      throw fail(
        0,
        "poll_abandoned",
        "The field read was left running.",
        null,
        { jobId: job.id },
      );
    }
    if (now() - began >= timeoutMs) {
      // Status 0 is this client's marker for "nothing answered", and
      // `retry.ts` treats it as the most retryable failure there is — which is
      // the right reading: the read may well still be running, and asking
      // again re-attaches to it rather than starting another.
      throw fail(
        0,
        "network_error",
        "The source is taking longer than expected to answer. Try reading it again.",
        null,
        { jobId: job.id },
      );
    }
    attempt += 1;
    await sleep(pollDelayMs(attempt));
    if (signal?.aborted) continue;
    onPoll?.(now() - began);
    try {
      job = await read(job.id);
      consecutiveFailures = 0;
    } catch (error) {
      consecutiveFailures += 1;
      if (consecutiveFailures > maxPollFailures) throw error;
      // Otherwise keep the last known state and ask again. The read is still
      // running on the server either way.
    }
  }

  if (job.status === "failed") {
    const failure = job.error;
    throw fail(
      failure?.status ?? 502,
      failure?.code ?? "unknown_error",
      failure?.message ?? "The field read failed.",
      failure?.field ?? null,
      failure?.details ?? {},
    );
  }

  if (job.result === null) {
    // A succeeded job with no result is a server-side contradiction rather
    // than anything the reviewer did. Named as such, instead of being handed
    // on as a null that breaks three screens further down.
    throw fail(
      502,
      "unknown_error",
      "The field read reported success without returning any fields.",
      null,
      {},
    );
  }
  return job.result;
}
